import React, { useEffect, useRef, useState } from 'react';
import { api, JiraStatus, JiraIssue, JiraProject } from './api';
import { Icon } from './icons';
import { Select } from './Select';

/** Jira panel — connect a personal Jira Cloud account (site URL + email + API token) and
 *  then search / create issues from chat. The connection is per-user and private: the API
 *  never returns the stored token, and every call runs against the caller's own account. */

const TOKEN_HELP = 'https://id.atlassian.com/manage-profile/security/api-tokens';
const CAT_COLOR: Record<string, { bg: string; fg: string }> = {
  new: { bg: 'rgba(120,130,150,0.22)', fg: 'var(--fx-text)' },
  indeterminate: { bg: 'rgba(56,132,255,0.22)', fg: '#4b8bff' },
  done: { bg: 'rgba(52,168,110,0.22)', fg: '#2ea36b' },
};

function relTime(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(t).toLocaleDateString();
}

export function JiraView() {
  const [status, setStatus] = useState<JiraStatus | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getJira().then((s) => { if (alive) setStatus(s); }).catch(() => { if (alive) setStatus({ connected: false }); });
    return () => { alive = false; };
  }, []);

  if (status === null) {
    return <Shell><div style={{ color: 'var(--fx-muted)', padding: 20 }}>Loading…</div></Shell>;
  }
  if (!status.connected) {
    return <Shell><ConnectCard onConnected={setStatus} /></Shell>;
  }

  return (
    <Shell>
      <ConnectedPanel
        status={status}
        onDisconnect={() => setStatus({ connected: false })}
        onNewIssue={() => setCreating(true)}
      />
      {creating && <NewIssueModal onClose={() => setCreating(false)} baseUrl={status.base_url || ''} />}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fx-chat" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {children}
    </div>
  );
}

// ── Connect form ───────────────────────────────────────────────────────────────────────
function ConnectCard({ onConnected }: { onConnected: (s: JiraStatus) => void }) {
  const [base, setBase] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setErr(null); setBusy(true);
    try {
      const s = await api.connectJira({ base_url: base.trim(), email: email.trim(), api_token: token.trim() });
      onConnected(s);
    } catch (e: any) {
      setErr(e?.message || 'Could not connect to Jira.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ width: 'min(460px, 94vw)', border: '1px solid var(--fx-hair)', borderRadius: 14, padding: 24, background: 'var(--fx-panel, rgba(128,128,128,0.05))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(56,132,255,0.16)', color: '#4b8bff', display: 'grid', placeItems: 'center' }}>
            <Icon name="tasks" size={19} />
          </span>
          <h1 style={{ margin: 0, fontSize: 19, fontWeight: 750, color: 'var(--fx-text)' }}>Connect Jira</h1>
        </div>
        <p style={{ color: 'var(--fx-muted)', fontSize: 13.5, lineHeight: 1.5, margin: '4px 0 18px' }}>
          Link your Jira Cloud account to search and create issues without leaving chat. Your
          connection is private to you — teammates can't see it.
        </p>

        <label style={lbl}>Jira site URL</label>
        <input className="fx-text-input" style={inp} value={base} onChange={(e) => setBase(e.target.value)}
          placeholder="https://yourcompany.atlassian.net" autoCapitalize="off" spellCheck={false} />

        <label style={lbl}>Account email</label>
        <input className="fx-text-input" style={inp} value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com" autoCapitalize="off" spellCheck={false} />

        <label style={lbl}>API token</label>
        <input className="fx-text-input" style={inp} value={token} onChange={(e) => setToken(e.target.value)}
          placeholder="Paste your Atlassian API token" type="password" autoComplete="off" spellCheck={false} />
        <div style={{ fontSize: 12, color: 'var(--fx-muted)', margin: '2px 0 16px' }}>
          Create one at{' '}
          <a href={TOKEN_HELP} target="_blank" rel="noopener noreferrer" style={{ color: '#4b8bff' }}>
            id.atlassian.com → Security → API tokens
          </a>. It's stored only to talk to your Jira.
        </div>

        {err && <div style={{ color: '#e56a6a', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        <button type="button" className="fx-sendbtn" disabled={busy} onClick={submit} style={{ width: '100%' }}>
          {busy ? 'Connecting…' : 'Connect Jira'}
        </button>
      </div>
    </div>
  );
}

// ── Connected: search + issue list ───────────────────────────────────────────────────────
function ConnectedPanel({ status, onDisconnect, onNewIssue }:
  { status: JiraStatus; onDisconnect: () => void; onNewIssue: () => void }) {
  const [issues, setIssues] = useState<JiraIssue[] | null>(null);
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number>(0);

  const load = (query: string, onlyMine: boolean) => {
    setErr(null);
    api.searchJiraIssues({ q: query, mine: onlyMine })
      .then(setIssues)
      .catch((e: any) => { setIssues([]); setErr(e?.message || 'Could not load issues.'); });
  };

  useEffect(() => { load(q, mine); /* eslint-disable-next-line */ }, [mine]);
  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => load(q, mine), 400);
    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [q]);

  const disconnect = async () => {
    if (!window.confirm('Disconnect Jira? You can reconnect any time.')) return;
    try { await api.disconnectJira(); } catch { /* ignore */ }
    onDisconnect();
  };

  return (
    <>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px 6px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 750, color: 'var(--fx-text)' }}>Jira</h1>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--fx-muted)' }}>
          {status.display_name || status.email}
        </span>
        <button type="button" onClick={disconnect} style={ghostBtn}>Disconnect</button>
        <button type="button" onClick={onNewIssue}
          style={{ border: 'none', borderRadius: 9, padding: '8px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', background: 'var(--fx-accent)', color: '#1b1300' }}>
          + New issue
        </button>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 20px 12px', flexWrap: 'wrap' }}>
        <input className="fx-text-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search issues…" style={{ flex: 1, minWidth: 180 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--fx-text)', cursor: 'pointer' }}>
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          Assigned to me
        </label>
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '0 20px 24px' }}>
        {err && <div style={{ color: '#e56a6a', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        {issues === null ? (
          <div style={{ color: 'var(--fx-muted)', fontSize: 14 }}>Loading…</div>
        ) : issues.length === 0 ? (
          <div style={{ color: 'var(--fx-muted)', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>
            {q || mine ? 'No matching issues.' : 'No issues found.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {issues.map((it) => <IssueRow key={it.key} it={it} />)}
          </div>
        )}
      </div>
    </>
  );
}

function IssueRow({ it }: { it: JiraIssue }) {
  const cat = CAT_COLOR[it.status_category || 'new'] || CAT_COLOR.new;
  return (
    <a href={it.url || '#'} target="_blank" rel="noopener noreferrer"
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 10,
        border: '1px solid var(--fx-hair)', textDecoration: 'none', color: 'var(--fx-text)', background: 'var(--fx-panel, transparent)' }}>
      <span style={{ fontFamily: 'ui-monospace,Menlo,Consolas,monospace', fontSize: 12.5, fontWeight: 700, color: '#4b8bff', flex: 'none' }}>{it.key}</span>
      <span style={{ flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.summary}</span>
      {it.assignee && <span style={{ fontSize: 12, color: 'var(--fx-muted)', flex: 'none' }}>{it.assignee}</span>}
      <span style={{ fontSize: 12, color: 'var(--fx-muted)', flex: 'none', minWidth: 54, textAlign: 'right' }}>{relTime(it.updated)}</span>
      {it.status && (
        <span style={{ flex: 'none', fontSize: 11.5, fontWeight: 650, padding: '3px 9px', borderRadius: 20, background: cat.bg, color: cat.fg }}>
          {it.status}
        </span>
      )}
    </a>
  );
}

// ── New issue modal ──────────────────────────────────────────────────────────────────────
function NewIssueModal({ onClose, baseUrl }: { onClose: () => void; baseUrl: string }) {
  const [projects, setProjects] = useState<JiraProject[] | null>(null);
  const [projectKey, setProjectKey] = useState('');
  const [type, setType] = useState('Task');
  const [summary, setSummary] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ key: string; url: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    api.listJiraProjects()
      .then((p) => { if (alive) { setProjects(p); if (p[0]) setProjectKey(p[0].key); } })
      .catch((e: any) => { if (alive) { setProjects([]); setErr(e?.message || 'Could not load projects.'); } });
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    if (busy || !projectKey || !summary.trim()) return;
    setErr(null); setBusy(true);
    try {
      const r = await api.createJiraIssue({ project_key: projectKey, summary: summary.trim(), description: desc.trim() || undefined, issue_type: type });
      setDone(r);
    } catch (e: any) {
      setErr(e?.message || 'Could not create the issue.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div className="fx-login-title">New Jira issue</div>

        {done ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 14, color: 'var(--fx-text)', marginBottom: 14 }}>
              Created{' '}
              <a href={done.url || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#4b8bff', fontWeight: 700 }}>{done.key}</a>.
            </div>
            <div className="fx-modal-actions" style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="fx-sendbtn" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>Project</label>
            <Select
              value={projectKey}
              onChange={setProjectKey}
              ariaLabel="Project"
              placeholder={projects === null ? 'Loading…' : 'Select a project'}
              options={(projects || []).map((p) => ({ value: p.key, label: `${p.name} (${p.key})` }))}
              style={{ width: '100%', marginBottom: 10 }}
            />

            <label style={lbl}>Type</label>
            <Select
              value={type}
              onChange={setType}
              ariaLabel="Issue type"
              options={['Task', 'Bug', 'Story', 'Epic', 'Subtask'].map((t) => ({ value: t, label: t }))}
              style={{ width: '100%', marginBottom: 10 }}
            />

            <label style={lbl}>Summary</label>
            <input className="fx-text-input" style={inp} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Short title for the issue" />

            <label style={lbl}>Description</label>
            <textarea className="fx-text-input" style={{ ...inp, minHeight: 96, resize: 'vertical' }} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional details" />

            {err && <div style={{ color: '#e56a6a', fontSize: 13, margin: '4px 0 10px' }}>{err}</div>}

            <div className="fx-modal-actions" style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
              <button type="button" className="fx-sendbtn" disabled={busy || !projectKey || !summary.trim()} onClick={submit}>
                {busy ? 'Creating…' : 'Create issue'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--fx-muted)', margin: '4px 0 5px' };
const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', marginBottom: 6 };
const ghostBtn: React.CSSProperties = { border: '1px solid var(--fx-hair)', background: 'transparent', color: 'var(--fx-text)', borderRadius: 9, padding: '7px 13px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
